import { getFormProps, useForm } from "@conform-to/react"
import { getZodConstraint, parseWithZod } from "@conform-to/zod"
import { invariantResponse } from "@epic-web/invariant"
import { type ActionFunctionArgs } from "react-router"
import { Link, data, useFetcher } from "react-router"
import { z } from "zod"
import { Button } from "#app/components/ui/button.js"
import {
	Dialog,
	DialogClose,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "#app/components/ui/dialog.tsx"
import { Checkbox } from "#app/components/ui/forms/conform-integrated/checkbox.js"
import {
	FormContext,
	FormControl,
	FormDescription,
	FormField,
	FormLabel,
} from "#app/components/ui/forms/conform-integrated/form.js"
import { setCookiePreferences } from "#app/utils/cookies-notice.server.js"
import routes from "../../../routes"

const CookiePreferencesSchema = z.object({
	interacted: z.boolean().optional(),
	necessary: z.boolean().optional(),
	analytics: z.boolean().optional(),
	targeting: z.boolean().optional(),
})

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	const submission = parseWithZod(formData, {
		schema: CookiePreferencesSchema,
	})

	invariantResponse(
		submission.status === "success",
		"Invalid preferences received",
	)

	const { interacted, necessary, analytics, targeting } = submission.value

	const responseInit = {
		headers: {
			"set-cookie": setCookiePreferences({
				interacted: interacted ?? true,
				necessary: necessary ?? true,
				analytics: analytics ?? false,
				targeting: targeting ?? false,
			}),
		},
	}
	return data({ result: submission.reply() }, responseInit)
}

export function CookiesNotice() {
	const fetcher = useFetcher<typeof action>()

	const [form, fields] = useForm({
		id: "cookies-notice",
		onValidate({ formData }) {
			const result = parseWithZod(formData, {
				schema: CookiePreferencesSchema,
			})
			return result
		},
		constraint: getZodConstraint(CookiePreferencesSchema),
		defaultValue: {
			interacted: true,
			necessary: true,
			analytics: true,
			targeting: true,
		},
		lastResult: fetcher.data?.result,
	})

	const formAction = "/resources/cookie-preferences"

	const CookiesPolicyLink = () => {
		return (
			<Link className="underline" to={routes.public.cookie_policy.route()}>
				Aviso de seguimiento y cookies en Greta's Patchwork
			</Link>
		)
	}

	return (
		<FormContext context={form.context}>
			<fetcher.Form
				method="POST"
				action={formAction}
				{...getFormProps(form)}
				className="fixed bottom-4 left-4 right-4 z-[20] flex flex-col gap-4 rounded-lg bg-secondary p-4 text-secondary-foreground md:flex-row md:items-center"
			>
				<p className="flex-1">
					Greta's Patchwork utiliza cookies para mejorar su experiencia de
					navegación, realizar análisis e investigaciones y realizar publicidad.
					Para cambiar sus preferencias, haga clic en 'Preferencias'. Puede
					aceptar todas las cookies haciendo clic en 'Aceptar'.{" "}
					<CookiesPolicyLink />
				</p>

				<div className="flex w-full flex-wrap items-center gap-xxs md:w-fit">
					<Dialog>
						<DialogTrigger asChild>
							<Button
								type="button"
								variant="link"
								className="flex w-full items-center gap-xxs md:w-auto"
							>
								Preferencias
							</Button>
						</DialogTrigger>
						<DialogContent className="max-h-[700px] max-w-[650px] overflow-y-auto">
							<DialogHeader>
								<DialogTitle>Preferencias de cookies</DialogTitle>
							</DialogHeader>

							<div className="flex flex-col gap-sm">
								<p>
									Respetamos su derecho a la privacidad, por lo que puede
									seleccionar qué cookies desea permitir. Sin embargo, siempre
									se permiten las cookies estrictamente necesarias, ya que son
									esenciales para que nuestros productos funcionen. Estas
									cookies no almacenan ninguna información de identificación
									personal ni son usadas para realizar un seguimiento de sus
									hábitos de navegación. <CookiesPolicyLink />
								</p>

								<FormField field={fields.necessary}>
									<div className="flex flex-col gap-2">
										<div className="flex items-start gap-2">
											<FormControl className="mt-2">
												<Checkbox disabled meta={fields.necessary} />
											</FormControl>
											<div>
												<FormLabel>Permitir las cookies necesarias</FormLabel>
												<FormDescription>
													Estas cookies permiten que nuestro sitio web funcione
													correctamente. Son estrictamente necesarias y por esa
													razón permanecen siempre habilitadas. Estas cookies
													siempre las configuramos nosotros, no terceros, y no
													rastrean tus hábitos de navegación.
												</FormDescription>
											</div>
										</div>
									</div>
								</FormField>

								<FormField field={fields.analytics}>
									<div className="flex flex-col gap-2">
										<div className="flex items-start gap-2">
											<FormControl className="mt-2">
												<Checkbox meta={fields.analytics} />
											</FormControl>
											<div>
												<FormLabel>Permitir cookies de análisis</FormLabel>
												<FormDescription>
													Estas cookies nos ayudan a mejorar nuestro sitio web
													al recopilar y generar información sobre su uso. Toda
													la información que recopilan estas cookies es agregada
													y, por lo tanto, anónima. Si no permite estas cookies,
													no sabremos cuándo ha visitado nuestro sitio y no
													podremos controlar su rendimiento.
												</FormDescription>
											</div>
										</div>
									</div>
								</FormField>

								<FormField field={fields.targeting}>
									<div className="flex flex-col gap-2">
										<div className="flex items-start gap-2">
											<FormControl className="mt-2">
												<Checkbox meta={fields.targeting} />
											</FormControl>
											<div>
												<FormLabel>Permitir cookies de publicidad</FormLabel>
												<FormDescription>
													Estas cookies son configuradas a través de nuestro
													sitio por nuestros socios publicitarios. Estas
													empresas pueden utilizarlas para crear un perfil de
													sus intereses y mostrarle anuncios relevantes en otros
													sitios. no almacenan información personal
													directamente, sino que se basan en la identificación
													única de su navegador y dispositivo de Internet. Si no
													permite estas cookies, experimentará publicidad menos
													dirigida.
												</FormDescription>
											</div>
										</div>
									</div>
								</FormField>
							</div>

							<DialogFooter>
								<DialogClose asChild>
									<Button variant="ghost">Cancelar</Button>
								</DialogClose>
								<Button
									type="button"
									onClick={() => {
										fetcher.submit(
											{ ...form.value },
											{
												method: "POST",
												action: formAction,
											},
										)
									}}
									className="flex w-full items-center gap-xxs md:w-auto"
								>
									Guardar
								</Button>
							</DialogFooter>
						</DialogContent>
					</Dialog>
					<Button
						type="button"
						onClick={() => {
							fetcher.submit(
								{ ...form.value },
								{
									method: "POST",
									action: formAction,
								},
							)
						}}
						className="flex w-full items-center gap-xxs md:w-auto"
					>
						Aceptar
					</Button>
				</div>
			</fetcher.Form>
		</FormContext>
	)
}
